<template>
  <div class="row justify-content-center">
    <transition name="fade" mode="out-in">
      <AppPlaceholder class="col-xl-8" v-if="!warehouse" key="no-warehouse" />
      <div v-else class="col-xl-8" key="warehouse">
        <div class="row">
          <div class="col-12 mb-4 d-sm-flex justify-content-between align-items-baseline">
            <div class="pl-3">
              <h4>
                {{ isNew ? "New warehouse" : warehouse.name }}
              </h4>
              <p class="mb-0 text-muted" v-if="!isNew">
                <small>Created on {{ warehouse.created_at | date }}</small>
              </p>
            </div>
            <div class="col-12 col-sm-auto text-right pr-0 mt-2 mt-sm-0">
              <button class="btn btn-primary mr-2" :disabled="loading" @click="save">
                Save
              </button>
              <button class="btn btn-outline-secondary" :disabled="loading" @click="saveAndClose">
                Save & close
              </button>
              <b-dropdown variant="link" no-caret right v-if="!isNew">
                <template v-slot:button-content>
                  <i class="material-icons">more_vert</i>
                </template>
                <b-dropdown-item :disabled="loading" @click="destroy">
                  Delete
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-body">
            <div class="row">
              <p class="col-12 text-muted mb-3"><strong>General</strong></p>
            </div>
            <div class="row">
              <AppInput
                label="Name"
                :value="warehouse.name"
                field="name"
                :errors="errors"
                @change="updateWarehouseProps({ name: $event })"
                class="col-md-6"
              />
              <AppInput
                label="Excise license number"
                :value="warehouse.license_no"
                field="license_no"
                :errors="errors"
                @change="updateWarehouseProps({ license_no: $event })"
                class="col-md-5"
              />
            </div>
            <div class="row">
              <AppSelect
                :value="warehouse.company || null"
                :multiple="false"
                :options="companiesSuggestions"
                track-by="id"
                label-field="name"
                label="Warehouse company:"
                placeholder=""
                @search-change="suggestCompanies($event)"
                @input="
                  updateWarehouseProps({ company_id: $event ? $event.id : null, company: $event })
                "
                class="col-md-6"
              />
              <AppInput
                label="Warehouse keeper number"
                :value="warehouse.keeper_no"
                field="keeper_no"
                :errors="errors"
                @change="
                  updateWarehouseProps({
                    keeper_no: $event
                  })
                "
                class="col-md-5"
              />
            </div>
            <div class="row">
              <AppSelect
                :value="warehouse.location"
                :options="locationSuggestions"
                :allow-empty="true"
                field="location_id"
                :errors="errors"
                track-by="id"
                label-field="name"
                :preserve-search="false"
                label="Location"
                placeholder=""
                @search-change="suggestLocations"
                @input="
                  updateWarehouseProps({
                    location_id: $event ? $event.id : null,
                    location: $event
                  })
                "
                class="col-md-11"
              >
                <template slot="noResult">
                  <button
                    @click="addLocation"
                    class="col-sm-12 btn btn-link"
                  >
                    Add Location
                  </button>
                </template>
              </AppSelect>
            </div>
          </div>
        </div>
        <LocationModal />
      </div>
    </transition>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import NotificationService from '../../services/notification.service';
import AppInput from '../form/AppInput';
import AppSelect from '../form/AppSelect';
import AppPlaceholder from '../form/AppPlaceholder';
import Errors from '../../utils/errors';
import { formatDate } from '../../filters/date.filter';
import LocationModal from '../locations/LocationModal';

export default {
  components: {
    AppInput,
    AppPlaceholder,
    AppSelect,
    BDropdown,
    BDropdownItem,
    LocationModal,
  },
  data() {
    return {
      errors: new Errors(),
      companiesSuggestions: [],
      locationSuggestions: [],
      loading: false,
      canClose: false,
    };
  },

  filters: {
    date: formatDate,
  },
  computed: {
    ...mapGetters({
      warehouse: 'warehouses/warehouse',
      countries: 'orders/countries',
    }),
    isNew() {
      return this.$route.params.id === 'new';
    },
  },
  mounted() {
    this.initWarehouse();
  },
  watch: {
    '$route.params.id'() {
      this.initWarehouse();
    },
  },
  methods: {
    // Start process of adding new location
    addLocation() {
      this.$store.dispatch('locations/openLocationModal', { type: 'job' }).then((res) => {
        this.updateWarehouseProps({
          location_id: res.id,
          location: res,
        });
      });
    },
    suggestLocations: debounce(function suggest(query) {
      return this.$store
        .dispatch('locations/suggestLocations', {
          query,
          type: 'job',
        })
        .then((suggestions) => {
          this.locationSuggestions = suggestions;
        });
    }, 300),
    initWarehouse() {
      if (this.isNew) {
        this.$store.dispatch('warehouses/newWarehouse');
      } else {
        this.getWarehouse();
      }
    },
    suggestCompanies: debounce(function suggest(query) {
      return this.$store
        .dispatch('companies/suggestCompanies', {
          query,
          type: 'warehouse',
        })
        .then((suggestions) => {
          this.companiesSuggestions = suggestions;
        });
    }, 300),
    getWarehouse() {
      this.$store.dispatch('warehouses/getWarehouse', this.$route.params.id);
    },
    updateWarehouseProps(props) {
      this.$store.dispatch('warehouses/updateWarehouseProps', props);
    },
    saveAndClose() {
      this.save().then(() => {
        if (this.canClose) {
          this.$router.push({ name: 'warehouses' });
        }
      });
    },
    save() {
      this.loading = true;
      this.errors.clear();
      return this.$store
        .dispatch(this.isNew ? 'warehouses/createWarehouse' : 'warehouses/updateWarehouse')
        .then((res) => {
          this.canClose = true;
          if (this.isNew) {
            this.$router.push({
              name: 'warehouse',
              params: { id: res.warehouse_id },
            });
          }
          NotificationService.success(res.message);
        })
        .catch((err) => {
          this.canClose = false;
          this.errors.set(err.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    destroy() {
      this.$bvModal.msgBoxConfirm('Delete warehouse?', { okTitle: 'Delete' }).then((value) => {
        if (value) {
          this.$store.dispatch('warehouses/deleteWarehouse').then((res) => {
            NotificationService.success(res.message);
            this.$router.push({ name: 'warehouses' });
          });
        }
      });
    },
  },
};
</script>
